import { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { tenderService } from "../../services/api";
// The platform's shared money formatter — apostrophe grouping for every
// currency, and CHF as a spaced text prefix (Marin, 2026-08-02).
import { formatCurrencyWithSymbol } from "../../utils/currencyFormatter";
// Same null-safe age range the request card uses (TenderCard.js:682), so the
// form and the card never disagree about a limit.
import { formatRange } from "../../utils/tenderFormatters";

const formatPosition = (position) => {
  return position
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

// Delegates to the shared formatter instead of its own Intl.NumberFormat, so
// this page groups with the platform's apostrophe and renders CHF like every
// other surface does. `!amount` keeps returning "-" — a 0 here has always read
// as "no figure given".
//
// NOTE: this helper is duplicated byte-for-byte in the Staff and the Coach
// pitch page. Both were changed identically; the duplication itself is older
// than this change and is left alone rather than refactored under a currency
// task.
const formatCurrency = (amount, currency = "USD") => {
  if (!amount) return "-";
  return formatCurrencyWithSymbol(amount, currency || "USD");
};

const StaffPitchTender = () => {
  const { tenderId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pitchContent, setPitchContent] = useState("");
  const [fileForApi, setFileForApi] = useState(null);

  // Get tender details from navigation state
  const tenderData = location.state?.tender;
  console.log(tenderData, "tenderData");

  // If no tender data, redirect back to tenders page
  useEffect(() => {
    if (!tenderData) {
      navigate("/staff/tenders");
    }
  }, [tenderData, navigate]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile({
        name: file.name,
        size: "1mb",
      });
      setFileForApi(file);
    }
  };

  const handleConfirmPitch = async () => {
    if (!pitchContent.trim()) {
      setError("Please add a message before submitting");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await tenderService.createStaffPitch({
        tender_id: tenderId,
        content: pitchContent,
        document: fileForApi
      });

      if (response.success) {
        setShowSuccessModal(true);
        setShowConfirmModal(false);
      } else {
        setError(response.error || "Failed to submit pitch. Please try again.");
      }
    } catch (err) {
      setError("Failed to submit pitch. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGoBack = () => {
    navigate("/staff/tenders");
  };

  const handleViewPitches = () => {
    navigate("/staff/tenders", { state: { activeTab: "applied" } });
  };

  const handleConfirmClick = () => {
    if (pitchContent.trim()) {
      setShowConfirmModal(true);
    } else {
      setError("Please add a message before submitting");
    }
  };

  const formatSalary = () => {
    if (tenderData.salary_type === "fixed_amount" && tenderData.fix_net_salary) {
      return formatCurrency(tenderData.fix_net_salary, tenderData.currency);
    } else if (tenderData.salary_type === "salary_between") {
      return `${formatCurrency(
        tenderData.net_salary_from,
        tenderData.currency
      )} - ${formatCurrency(tenderData.net_salary_to, tenderData.currency)}`;
    }
    return "-";
  };

  const formatMarketValue = () => {
    if (tenderData.market_value_type === "market_value_range") {
      return `${formatCurrency(
        tenderData.market_value_from,
        tenderData.currency
      )} - ${formatCurrency(tenderData.market_value_to, tenderData.currency)}`;
    } else if (tenderData.fix_market_value) {
      return formatCurrency(tenderData.fix_market_value, tenderData.currency);
    }
    return "-";
  };

  return (
    <div className="min-h-screen bg-white py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8 px-14">
        <div className="flex items-center gap-2">
          <button onClick={() => navigate(-1)} className="text-gray-600">
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="#2C2C2C"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-[#202630]">Apply for Request</h1>
        </div>
        <div className="flex gap-4">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-[#1E1E1E] hover:bg-gray-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirmClick}
            className={`px-4 py-2 rounded-lg ${
              pitchContent.trim()
                ? "bg-[#2283FF] text-white hover:bg-blue-600"
                : "bg-[#2283FF4D] text-white cursor-not-allowed"
            }`}
          >
            Confirm
          </button>
        </div>
      </div>

      {/* Tender Details */}
      {tenderData && (
        <div className="px-14 bg-gradient-to-r from-[rgba(244,227,237,0.1)] to-[rgba(246,167,85,0.1)] border-y border-[#D2D4D880] p-6 mb-4">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <div>
                <img
                  src={tenderData.club.logo}
                  alt={tenderData.club.name}
                  className="w-12 h-12"
                />
              </div>
              <div>
                <h2 className="text-lg text-[#222A35] font-semibold mb-1">
                  Request for {formatPosition(tenderData.position)}
                </h2>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-[#222A35]">
                    By {tenderData.club.name}
                  </span>
                </div>
              </div>
            </div>
            {/* Attached-document paperclip — mirrors the agent-twin pattern in
                PitchPlayer.js. Renders only when the request has a document
                (QAV5-402: was a dead <button>, never carried document_url). */}
            {tenderData.document_url && (
              <a
                href={tenderData.document_url}
                target="_blank"
                rel="noreferrer"
                className="py-2 px-4 hover:bg-gray-100 bg-white rounded-lg"
              >
                <svg
                  width="11"
                  height="20"
                  viewBox="0 0 11 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.5 20C3.96667 20 2.66667 19.4667 1.6 18.4C0.533333 17.3333 0 16.0333 0 14.5V4C0 2.9 0.391667 1.95833 1.175 1.175C1.95833 0.391667 2.9 0 4 0C5.1 0 6.04167 0.391667 6.825 1.175C7.60833 1.95833 8 2.9 8 4V13.5C8 14.2 7.75833 14.7917 7.275 15.275C6.79167 15.7583 6.2 16 5.5 16C4.8 16 4.20833 15.7583 3.725 15.275C3.24167 14.7917 3 14.2 3 13.5V4H4.5V13.5C4.5 13.7833 4.59583 14.0208 4.7875 14.2125C4.97917 14.4042 5.21667 14.5 5.5 14.5C5.78333 14.5 6.02083 14.4042 6.2125 14.2125C6.40417 14.0208 6.5 13.7833 6.5 13.5V4C6.5 3.3 6.25833 2.70833 5.775 2.225C5.29167 1.74167 4.7 1.5 4 1.5C3.3 1.5 2.70833 1.74167 2.225 2.225C1.74167 2.70833 1.5 3.3 1.5 4V14.5C1.5 15.6 1.89167 16.5417 2.675 17.325C3.45833 18.1083 4.4 18.5 5.5 18.5C6.6 18.5 7.54167 18.1083 8.325 17.325C9.10833 16.5417 9.5 15.6 9.5 14.5V4H11V14.5C11 16.0333 10.4667 17.3333 9.4 18.4C8.33333 19.4667 7.03333 20 5.5 20Z"
                    fill="#1D1B20"
                  />
                </svg>
              </a>
            )}
          </div>

          <p className="text-sm text-[#535370] mb-6">
            {tenderData.description || "Description Not Available"}
          </p>

          {/* Wraps below md only: the five tiles need 438px of min-content but a
              375px phone leaves 263px inside the px-14 gutters, so Yearly Salary
              and Languages Known used to sit past the right edge. From 768 up
              the row keeps its previous single-line behaviour untouched. */}
          <div className="flex flex-wrap md:flex-nowrap bg-white justify-between gap-4">
            <div className="p-4 rounded-lg flex flex-col gap-4">
              <p className="text-sm text-secondary">Transfer Fee</p>
              <p className="font-medium">
                {formatMarketValue()}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
              <p className="text-sm text-secondary">Request Type</p>
              <span className="px-3 py-1 bg-emerald-100 text-emerald-600 rounded-full text-sm">
                {tenderData.type_of_tender}
              </span>
            </div>
            <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
              <p className="text-sm text-secondary">Age Limit</p>
              <p className="font-medium">
                {formatRange(tenderData.min_age, tenderData.max_age, "years") || "-"}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
              {/* Monthly: the request form captures the number per month
                  (TenderFormDetails.js:542,578). */}
              <p className="text-sm text-secondary">Monthly salary</p>
              <p className="font-medium">
                {formatSalary()}
              </p>
            </div>
            <div className="bg-white p-4 rounded-lg flex flex-col gap-4">
              <p className="text-sm text-secondary">Languages Known</p>
              {/* Reads the request's own languages, same rule as the card
                  (StaffTenderCard.js:303-308): joined when present, "-" when
                  the list is empty. Used to be a hardcoded "English, Spanish"
                  left over from the mock fixture (agentTenderMock.js:50). */}
              <p className="font-medium">
                {tenderData.language_skills && tenderData.language_skills.length > 0
                  ? tenderData.language_skills.join(", ")
                  : "-"}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Add Message Section */}
      <div className="bg-[#F8F9FB] border border-[#D6DBEA] rounded-xl p-6 mx-14 mb-6">
        <h2 className="text-[18px] font-medium text-[#000000] mb-2">
          Add Message
        </h2>
        <p className="text-[14px] text-[#000000CC] mb-6">
          Add a brief message introducing yourself and attach relevant documents (e.g. CV, certifications, past experience)
        </p>

        {/* Message Input */}
        <div className="mb-6">
          <textarea
            value={pitchContent}
            onChange={(e) => setPitchContent(e.target.value)}
            placeholder="Add your message here"
            className="w-full p-4 border-2 border-[#CBD0DC] bg-white rounded-lg resize-none focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
            rows={6}
          />
        </div>

        {/* File Upload Section */}
        <div className="flex flex-col gap-4 ">
          <div className="flex items-center gap-2">
            <span className="text-[16px] font-medium text-[#292D32]">
              Choose a file or drag & drop it here
            </span>
            <span className="text-[16px] font-medium text-[#1A212F99]">
              PDFs only, up to 50MB
            </span>
          </div>
          <button
            onClick={() => document.getElementById('file-upload').click()}
            className="w-fit border border-[#E6E6E6] bg-white px-4 py-2 text-[#1E1E1E] rounded-lg "
          >
            Upload file
          </button>
          <input
            id="file-upload"
            type="file"
            accept=".pdf"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>

        {/* Show uploaded file */}
        {uploadedFile && (
          <div className="mt-4 p-3 bg-white border border-gray-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                </svg>
                <span className="text-sm font-medium">{uploadedFile.name}</span>
                <span className="text-xs text-gray-500">({uploadedFile.size})</span>
              </div>
              <button
                onClick={() => {
                  setUploadedFile(null);
                  setFileForApi(null);
                }}
                className="text-red-500 hover:text-red-700"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl py-8 px-14 mx-4 max-w-md">
            <h2 className="text-xl font-bold text-center mb-6 text-[#1A212F]">
              Confirm Pitch Submission
            </h2>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to submit your pitch for this request?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-2 px-4 border border-[#E6E6E6] rounded-lg text-[#1E1E1E] text-sm shadow-sm hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPitch}
                disabled={loading}
                className="flex-1 py-2 px-4 bg-[#2283FF] text-white rounded-lg text-sm shadow-md hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? "Submitting..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl py-8 px-14 mx-4 max-w-md">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-xl font-bold mb-4 text-[#1A212F]">
                Pitch Submitted Successfully!
              </h2>
              <p className="text-gray-600 mb-6">
                Your pitch has been submitted and is now under review.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleGoBack}
                  className="flex-1 py-2 px-4 border border-[#E6E6E6] rounded-lg text-[#1E1E1E] text-sm shadow-sm hover:bg-gray-50"
                >
                  Back to Requests
                </button>
                <button
                  onClick={handleViewPitches}
                  className="flex-1 py-2 px-4 bg-[#2283FF] text-white rounded-lg text-sm shadow-md hover:bg-blue-600"
                >
                  View My Pitches
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffPitchTender;